import { z } from 'zod';
import { AiAdvanceValidationError } from '../../ai-platform/ports.js';

export const CDLIT_BOOKING_CLIENT_PAYLOAD_TTL_HOURS = 24;

export const cdlitAiBookingAttemptStatusSchema = z.enum([
  'draft',
  'ready_to_confirm',
  'creating',
  'confirmed',
  'slot_conflict',
  'failed',
  'expired',
]);

export type CdlitAiBookingAttemptStatus = z.infer<typeof cdlitAiBookingAttemptStatusSchema>;

export const cdlitBookingFailureCodeSchema = z.enum([
  'slot_conflict',
  'booking_routing_unconfigured',
  'booking_write_not_verified',
  'booking_not_enabled',
  'validation_error',
  'upstream_unavailable',
  'booking_outcome_unknown',
]);

export type CdlitBookingFailureCode = z.infer<typeof cdlitBookingFailureCodeSchema>;

export const cdlitBookingSelectionSchema = z
  .object({
    serviceExternalId: z.string().min(1).max(64),
    staffExternalId: z.string().min(1).max(64),
    slotStartAt: z.string().datetime(),
  })
  .strict();

export type CdlitBookingSelection = z.infer<typeof cdlitBookingSelectionSchema>;

export const cdlitBookingClientInputSchema = z
  .object({
    fullname: z.string().min(1).max(200),
    phone: z.string().min(5).max(32),
    email: z.string().email().max(200).optional(),
  })
  .strict();

export type CdlitBookingClientInput = z.infer<typeof cdlitBookingClientInputSchema>;

export const cdlitBookingClientAgreementsSchema = z
  .object({
    isPersonalDataProcessingAllowed: z.boolean(),
    isNewsletterAllowed: z.boolean().optional(),
  })
  .strict();

export type CdlitBookingClientAgreements = z.infer<typeof cdlitBookingClientAgreementsSchema>;

export const cdlitBookingConfirmationViewSchema = z
  .object({
    serviceTitle: z.string().min(1).max(200),
    staffTitle: z.string().min(1).max(200),
    slotStartAt: z.string().datetime(),
  })
  .strict();

export type CdlitBookingConfirmationView = z.infer<typeof cdlitBookingConfirmationViewSchema>;

export const cdlitBookingAttemptPublicViewSchema = z
  .object({
    attemptId: z.string().uuid(),
    status: cdlitAiBookingAttemptStatusSchema,
    expiresAt: z.string().datetime(),
    failureCode: cdlitBookingFailureCodeSchema.nullable().optional(),
    confirmation: cdlitBookingConfirmationViewSchema.optional(),
  })
  .strict();

export type CdlitBookingAttemptPublicView = z.infer<typeof cdlitBookingAttemptPublicViewSchema>;

export const cdlitBookingPrepareRequestSchema = z
  .object({
    idempotencyKey: z.string().min(8).max(128),
  })
  .strict();

export type CdlitBookingPrepareRequest = z.infer<typeof cdlitBookingPrepareRequestSchema>;

export const cdlitBookingConfirmRequestSchema = z
  .object({
    client: cdlitBookingClientInputSchema,
    phoneConfirmationCode: z.string().min(4).max(16).optional(),
    clientAgreements: cdlitBookingClientAgreementsSchema.optional(),
  })
  .strict();

export type CdlitBookingConfirmRequest = z.infer<typeof cdlitBookingConfirmRequestSchema>;

export const CDLIT_BOOKING_FORBIDDEN_BODY_FIELDS = [
  'productKey',
  'tenantKey',
  'bookingCompanyId',
  'companyId',
  'yclientsRecordId',
  'systemPrompt',
  'safeSummary',
  'transcript',
  'diagnosis',
  'medication',
  'sessionId',
  'selection',
  'serviceExternalId',
  'staffExternalId',
  'slotStartAt',
  'serviceId',
  'staffId',
  'slot',
  'staffTitle',
  'serviceTitle',
  'staffDisplayName',
] as const;

export function assertNoForbiddenBookingBodyFields(body: unknown): void {
  if (body == null || typeof body !== 'object' || Array.isArray(body)) return;
  for (const key of CDLIT_BOOKING_FORBIDDEN_BODY_FIELDS) {
    if (key in body) {
      throw new AiAdvanceValidationError(`Field ${key} is not allowed`);
    }
  }
}

export type CdlitBookingVerifySlotStatus = 'available' | 'slot_unavailable' | 'availability_unavailable';

export type CdlitBookingVerifySlotResult = {
  status: CdlitBookingVerifySlotStatus;
};

export type CdlitBookingVerifySlotInput = {
  bookingCompanyId: string;
  serviceExternalId: string;
  staffExternalId: string;
  slotStartAt: string;
};

export type CdlitBookingCreateRecordInput = CdlitBookingVerifySlotInput & {
  client: CdlitBookingClientInput;
  safeSummary: string | null;
  /** YClients `book_times[].datetime` — required verbatim for `book_record` per API docs. */
  yclientsAppointmentDatetime?: number | string;
  /** Required when salon `phone_confirmation` / `confirm_number` is enabled in YClients settings. */
  phoneConfirmationCode?: string;
  clientAgreements?: CdlitBookingClientAgreements;
};

export type CdlitBookingCreateRecordResult = {
  recordId: string;
  recordHash: string;
};

export interface CdlitBookingAdapter {
  verifySlot(input: CdlitBookingVerifySlotInput): Promise<CdlitBookingVerifySlotResult>;
  createRecord(input: CdlitBookingCreateRecordInput): Promise<CdlitBookingCreateRecordResult>;
}

export class CdlitBookingError extends Error {
  constructor(
    public readonly code: CdlitBookingFailureCode | 'booking_not_enabled',
    message?: string,
  ) {
    super(message ?? code);
    this.name = 'CdlitBookingError';
  }
}

export type CdlitBookingAdapterFailureKind = 'slot_conflict' | 'validation_error' | 'unknown_outcome';

/** Typed adapter failure — distinct from generic upstream_unavailable. */
export class CdlitBookingAdapterError extends Error {
  constructor(
    public readonly kind: CdlitBookingAdapterFailureKind,
    message?: string,
  ) {
    super(message ?? kind);
    this.name = 'CdlitBookingAdapterError';
  }
}
